import { ClockCircleOutlined, EnvironmentOutlined, MoreOutlined } from "@ant-design/icons"
import { Button, Col, Dropdown, Flex, Row, Space, Spin } from "antd"
import { EXPERIENCE_STATES, EXPERIENCE_TYPES } from "constants/experiences"
import PropTypes from "prop-types"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Paragraph, Text, Title } from "ui/Typography"
import { getActionConditions, getDropdownItems, formatExperienceTerms } from "../../utils/experience"
import styles from "./ExperienceCard.module.css"
import { useNavigate } from "react-router-dom"
import { numberToCurrency } from "helpers/string"
import { routes } from "router"
import { getMenuConfig } from "./MenuConfig"
import ImagePlaceholder from "components/common/ImagePlaceholder"
import UserSvg from "assets/icons/ex-user.svg"
import PricingDetailsTooltip from "ui/PricingDetailsTooltip"
import { MODULES, ACTIONS } from "utils/permissions"
import { usePermissions } from "hooks/usePermissions"
import { SingleTicketIcon } from "ui/Icon/DSIcon/SingleTicket"
import { CalendarOutlineIcon } from "ui/Icon/DSIcon/CalendarOutline"
import { MultiTicketsIcon } from "ui/Icon/DSIcon/MultiTickets"
import { RentalIcon } from "ui/Icon/DSIcon/RentalIcon"
import { useMediaQuery } from "react-responsive"
import CustomDrawer from "ui/Drawer"
import { spacings } from "themes/variables"

export const ExperienceCard = ({
  trip,
  onShare,
  onDuplicate,
  onDeactivate,
  onDelete,
  onPublish,
  onEditCategory,
  actionsDisabled,
  isDuplicating,
  loadingActionKey
}) => {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" })
  const [isActionDrawerOpen, setIsActionDrawerOpen] = useState(false)

  const {
    title,
    status,
    address,
    min_price,
    max_guests,
    publish_on_guides_site,
    publish_on_platform,
    trip_prices,
    duration_type,
    duration_value,
    seasonal
  } = trip || {}

  const price = min_price || 0
  const published = status === EXPERIENCE_STATES.published

  const menuConfig = useMemo(
    () =>
      getMenuConfig(t, {
        onShare,
        onDuplicate,
        onDeactivate,
        onDelete,
        onPublish,
        onEditCategory
      }),
    [t, onShare, onDuplicate, onDeactivate, onDelete, onPublish, onEditCategory]
  )

  const conditions = useMemo(() => getActionConditions(status), [status])
  const dropdownItems = useMemo(() => getDropdownItems(menuConfig, conditions), [menuConfig, conditions])
  const navigate = useNavigate()

  const handleEdit = () => {
    const isDraft = status === EXPERIENCE_STATES.draft
    const listingType = trip?.experience_type === EXPERIENCE_TYPES.ticketed ? EXPERIENCE_TYPES.ticketed : EXPERIENCE_TYPES.private
    if (isDraft) {
      navigate(`${routes.proNewExperienceV2Path({ id: trip?.id })}?type=${listingType}`)
    } else {
      const routeType = status === EXPERIENCE_STATES.deactivated ? "deactivated" : "published"
      const baseUrl = routes.proEditExperienceV2Path({ id: trip?.id })
      navigate(`${baseUrl}?route=${routeType}&type=${listingType}`)
    }
  }

  const handlePreviewClick = () => {
    const isDraft = status === EXPERIENCE_STATES.draft
    if (isDraft) {
      navigate(routes.proNewExperienceV2PreviewPath({ id: trip?.id }))
    } else {
      const routeType = status === EXPERIENCE_STATES.deactivated ? "deactivated" : "published"
      const baseUrl = routes.proEditExperienceV2PreviewPath({ id: trip?.id })
      navigate(`${baseUrl}&readOnlyPreview=true&route=${routeType}`)
    }
  }

  const isDeactivated = status === EXPERIENCE_STATES.deactivated

  let filteredDropdownItems = isDeactivated
    ? dropdownItems.filter((item) => item.key === "publish" || item.key === "delete")
    : dropdownItems

  filteredDropdownItems = filteredDropdownItems.filter((item) => {
    switch (item.key) {
      case "deactivate":
        return hasPermission(MODULES.EXPERIENCES, ACTIONS.EDIT)
      case "publish":
        return trip?.can_edit
      case "delete":
        return trip?.can_edit
      case "duplicate":
        return hasPermission(MODULES.EXPERIENCES, ACTIONS.CREATE)
      case "edit_category":
        return trip?.can_edit && status === EXPERIENCE_STATES.published
      case "share":
      default:
        return true
    }
  })

  const mainPhoto = useMemo(() => {
    const photos = trip?.photos || []
    return Array.isArray(photos) ? photos.find((p) => p.position === 0) : null
  }, [trip?.photos])

  const experienceTypeIcon = useMemo(() => {
    const rawType = trip?.trip_type || trip?.experience_type || trip?.experienceType || trip?.type
    const normalizedType = String(rawType || "private").toLowerCase()
    const iconMap = {
      private: <SingleTicketIcon fill="var(--color-primary)" />,
      ticketed: <MultiTicketsIcon fill="var(--color-primary)" />,
      rental: <RentalIcon fill="var(--color-primary)" />,
      rentals: <RentalIcon fill="var(--color-primary)" />
    }

    return iconMap[normalizedType] || iconMap.private
  }, [trip?.trip_type, trip?.experience_type, trip?.experienceType, trip?.type])

  const menuItems = useMemo(
    () =>
      filteredDropdownItems.map((item) => {
        // Show a spinner on whichever action is running for this card: duplicate (legacy
        // prop) or any other action via loadingActionKey (deactivate, publish, delete).
        const isItemLoading = (item.key === "duplicate" && isDuplicating) || item.key === loadingActionKey
        return {
          ...item,
          disabled: actionsDisabled,
          label: isItemLoading ? (
            <Space size={8}>
              <Spin size="small" />
              {item.label}
            </Space>
          ) : (
            item.label
          )
        }
      }),
    [filteredDropdownItems, actionsDisabled, isDuplicating, loadingActionKey]
  )

  const handleMenuAction = (key) => {
    const item = menuItems?.find((i) => i.key === key)
    if (item && typeof item.onClick === "function") {
      item.onClick()
    }
    setIsActionDrawerOpen(false)
  }

  return (
    <div className={styles.cardWrapper}>
      <div className={styles.imgWrapper}>
        {experienceTypeIcon && <div className={`${styles.statusBadge} ${styles.experienceTypeBadge}`}>{experienceTypeIcon}</div>}
        {seasonal && (
          <Flex align="center" justify="center" gap={4} className={styles.seasonalBadge}>
            <CalendarOutlineIcon />
            {t("experiences.seasonal.label")}
          </Flex>
        )}
        {(status === EXPERIENCE_STATES.draft || isDeactivated) && (
          <div className={styles.stateBadge}>
            {status === EXPERIENCE_STATES.draft ? t("trip.card.draft") : t("experiences.deactivated")}
          </div>
        )}
        {mainPhoto ? (
          <>
            {/* Blurred background layer */}
            <img src={mainPhoto.preview_url} alt="" className={styles.blurredBackground} />
            {/* Main media content */}
            <Flex justify="center" align="center" className={styles.mediaContent}>
              <img src={mainPhoto.preview_url} alt={title} className={styles.img} />
            </Flex>
          </>
        ) : (
          <Flex justify="center" align="center" className={styles.mediaContent}>
            <ImagePlaceholder className={`h-full ${styles.img}`} label={false} />
          </Flex>
        )}
      </div>
      <div className={styles.content}>
        <div>
          <Row justify="space-between" align="top">
            <Col>
              <Title level={5} style={{ margin: 0, fontWeight: 500 }} className="pb-xs">
                {title}
              </Title>
            </Col>
            <Col>
              <Flex gap={8} align="center">
                <Text className="color-text-secondary">from</Text>
                <Text className="text-color-primary" strong>
                  {numberToCurrency(price)}
                </Text>
                <PricingDetailsTooltip tripPrices={trip_prices} durationType={duration_type} durationValue={duration_value} />
              </Flex>
            </Col>
          </Row>
          <Row justify="space-between" align="top">
            {address && (
              <Col xxl={14} xl={14} span={24} className="locationText">
                <Space align="top">
                  <EnvironmentOutlined className="color-text-secondary-dark" />
                  <Paragraph ellipsis={{ rows: 2 }} className="mb-0 color-text-secondary-dark">
                    {address}
                  </Paragraph>
                </Space>
              </Col>
            )}
            <Col>
              <Space size={16}>
                <Space>
                  <img src={UserSvg} alt="" />
                  <Text className="lowercased color-text-secondary-dark">
                    {max_guests} {t("global.max")}
                  </Text>
                </Space>
                <Space>
                  <ClockCircleOutlined />
                  <Text className="color-text-secondary-dark">{formatExperienceTerms(trip, t)}</Text>
                </Space>
              </Space>
            </Col>
          </Row>
          {/* Always render this row so card height/spacing stays consistent
              whether or not categories exist. */}
          <Row className="mt-xs">
            <Col span={24}>
              <Flex gap={6} wrap="wrap" className={styles.categoryRow}>
                {Array.isArray(trip?.categories) &&
                  trip.categories.map((cat, index) => {
                    const label = typeof cat === "object" ? (cat?.name ?? cat?.label) : String(cat)
                    const key = typeof cat === "object" ? (cat?.id ?? index) : `${cat}-${index}`
                    return (
                      <span key={key} className={styles.categoryChip}>
                        {label}
                      </span>
                    )
                  })}
              </Flex>
            </Col>
          </Row>
          {published && (
            <Row className="mt-xs">
              <Col span={24}>
                <Text variant="caption" className={styles.statusLine}>
                  {t("experiences.empty_state.published")}
                  {(publish_on_platform || publish_on_guides_site) && (
                    <>
                      <span className={styles.statusSeparator}>·</span>
                      {[publish_on_platform && t("global.ds_marketplace"), publish_on_guides_site && t("global.frontend", { defaultValue: "Frontend" })]
                        .filter(Boolean)
                        .join(", ")}
                    </>
                  )}
                </Text>
              </Col>
            </Row>
          )}
        </div>
        <Row justify="space-between" align="middle" className={styles.publishCard}>
          <Col>
            <Space>
              {trip?.can_edit && (
                <Button color="primary" variant="outlined" onClick={handleEdit} disabled={actionsDisabled}>
                  {status === EXPERIENCE_STATES.draft ? t("global.continue_creating") : t("global.edit")}
                </Button>
              )}
              {status !== EXPERIENCE_STATES.draft && (
                <Button color="primary" variant="outlined" onClick={handlePreviewClick} disabled={actionsDisabled}>
                  {t("global.preview")}
                </Button>
              )}
            </Space>
          </Col>
          <Col>
            {isMobile ? (
              <>
                <div className={styles.actionButtonWrapper}>
                  <Button
                    color="primary"
                    variant="text"
                    icon={<MoreOutlined />}
                    disabled={actionsDisabled}
                    loading={isDuplicating || !!loadingActionKey}
                    onClick={() => setIsActionDrawerOpen(true)}
                  />
                </div>
                <CustomDrawer open={isActionDrawerOpen} setOpen={setIsActionDrawerOpen} title="More Actions">
                  <Flex vertical gap={spacings.S}>
                    {menuItems.map((item) => (
                      <Button
                        key={item.key}
                        className="w-full"
                        color={item.danger ? "danger" : "primary"}
                        variant="outlined"
                        onClick={() => handleMenuAction(item.key)}
                        disabled={item.disabled}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Flex>
                </CustomDrawer>
              </>
            ) : (
              <Dropdown
                menu={{
                  items: menuItems,
                  onClick: (info) => {
                    handleMenuAction(info.key)
                  }
                }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <div className={styles.actionButtonWrapper}>
                  <Button color="primary" variant="text" icon={<MoreOutlined />} disabled={actionsDisabled} loading={isDuplicating || !!loadingActionKey} />
                </div>
              </Dropdown>
            )}
          </Col>
        </Row>
      </div>
    </div>
  )
}
ExperienceCard.propTypes = {
  trip: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.oneOf(Object.values(EXPERIENCE_STATES)).isRequired,
    min_price: PropTypes.number,
    address: PropTypes.string,
    max_guests: PropTypes.number.isRequired,
    trip_length: PropTypes.oneOf(["full_day", "half_day"]),
    available_hours: PropTypes.arrayOf(
      PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string
      })
    ),
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        preview_url: PropTypes.string.isRequired
      })
    ),
    cropped_photos: PropTypes.arrayOf(
      PropTypes.shape({
        preview_url: PropTypes.string.isRequired
      })
    ),
    publish_on_guides_site: PropTypes.bool,
    publish_on_platform: PropTypes.bool,
    seasonal: PropTypes.bool,
    available_hours_range: PropTypes?.string,
    trip_prices: PropTypes.arrayOf(
      PropTypes.shape({
        price: PropTypes.number,
        guests: PropTypes.number,
        pricing_type: PropTypes.string,
        included_guests: PropTypes.number,
        guests_included_in_flat_rate: PropTypes.number,
        price_per_person: PropTypes.number,
        price_per_child: PropTypes.number,
        price_per_infant: PropTypes.number,
        price_per_passenger: PropTypes.number
      })
    ),
    can_edit: PropTypes.bool,
    experience_type: PropTypes.string,
    experienceType: PropTypes.string,
    trip_type: PropTypes.string,
    type: PropTypes.string,
    categories: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string
      })
    )
  }).isRequired,
  uiType: PropTypes.oneOf(["draft", "published", "deactivated", "unpublished"]),
  onShare: PropTypes.func,
  onDuplicate: PropTypes.func,
  onDeactivate: PropTypes.func,
  onDelete: PropTypes.func,
  onPublish: PropTypes.func,
  onEditCategory: PropTypes.func,
  actionsDisabled: PropTypes.bool,
  isDuplicating: PropTypes.bool,
  loadingActionKey: PropTypes.string
}

ExperienceCard.defaultProps = {
  uiType: "published",
  onShare: () => {},
  onDuplicate: () => {},
  onDeactivate: () => {},
  onDelete: () => {},
  onPublish: () => {},
  onEditCategory: () => {},
  actionsDisabled: false,
  isDuplicating: false,
  loadingActionKey: null
}
